import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";

import { clearStoredTableState } from "@punto/frontend-shared/hooks/use-table-state";
import { useTranslation } from "@punto/frontend-shared/i18n/i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@punto/ui/components/select";

import { useUserOrganizations } from "@/modules/auth/hooks/use-user-organizations";
import { clearStoredCustomerScope } from "@/modules/customers/hooks";

/**
 * The label for an org row/trigger: a single-customer org is labelled by its
 * customer name (more recognizable than the umbrella org name); an org grouping
 * several customers keeps the org name (with the customers shown as a subtitle).
 */
const organizationLabel = (org: {
  name: string;
  customerNames: string[];
}): string =>
  org.customerNames.length === 1 ? org.customerNames[0] : org.name;

export const OrganizationSelector = () => {
  const { t } = useTranslation();
  const { loginWithRedirect } = useAuth0();
  const { currentOrganization, organizations } = useUserOrganizations();

  const handleOrganizationChange = useCallback(
    async (organizationId: string) => {
      if (organizationId === currentOrganization?.referenceId) return;

      // Filters belong to the organization they were built in — a stored
      // customer id from the old org comes back as a filter the user can no
      // longer see and nothing matches. Dropping them is also what makes the
      // landing URL come back clean: the Auth0 round-trip returns to "/" with no
      // query string, but `LandingRedirect` sends the user straight on to
      // /procedures, where session storage would immediately rewrite `?state=`.
      clearStoredTableState();
      clearStoredCustomerScope();

      await loginWithRedirect({
        appState: {
          returnTo: "/",
        },
        authorizationParams: {
          organization: organizationId,
        },
      });
    },
    [loginWithRedirect, currentOrganization?.referenceId],
  );

  if (organizations.length < 2) {
    return null;
  }

  return (
    <Select
      value={currentOrganization?.referenceId}
      onValueChange={(value) => void handleOrganizationChange(value)}
    >
      {/* Collapsed trigger stays a single line to keep the header compact; the
          customer detail lives in the dropdown options. Below `md` this can
          render inside the mobile nav Sheet (`w-3/4` with `p-6`), which at a
          320px viewport leaves less room than the desktop `min-w-[200px]`
          floor — so it shrinks to fit its container there and only takes the
          fixed desktop sizing back at `md`+, where it lives in the header
          bar (P-2683). */}
      <SelectTrigger className="w-full min-w-0 md:w-auto md:min-w-[200px] md:max-w-[250px]">
        <span className="truncate">
          {currentOrganization
            ? organizationLabel(currentOrganization)
            : t("OrganizationSelector.placeholder")}
        </span>
      </SelectTrigger>
      <SelectContent className="max-w-[300px]">
        {organizations.map((org) => {
          // Only surface the subtitle when an org groups MORE THAN ONE customer;
          // a single customer is already shown as the row's label.
          const showCustomers = org.customerNames.length > 1;
          const customerList = org.customerNames.join(", ");
          return (
            <SelectItem
              key={org.referenceId}
              value={org.referenceId}
              textValue={organizationLabel(org)}
              // Native tooltip shows the full customer list on hover; it is
              // browser-positioned so it can't be clipped by the dropdown.
              title={showCustomers ? customerList : undefined}
            >
              <div className="flex flex-col">
                <span>{organizationLabel(org)}</span>
                {showCustomers && (
                  // Fixed max-w so the ellipsis engages without depending on the
                  // ItemText wrapper's min-w-0 chain.
                  <span className="block max-w-[240px] truncate text-xs text-muted-foreground">
                    {org.customerNames.join(" · ")}
                  </span>
                )}
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};
